import { Milliseconds } from '@ankr.com/utils/unit';

import { IApiUserGroupParams } from '../../accounting/userGroup';
import { Web3Address } from '../../common';

export enum EGlobalNotificationType {
  BALANCE_ALARM = 'BALANCE_ALARM',
  SUPER_RED_ALERT = 'SUPER_RED_ALERT',
  ACCOUNT_SUSPENDED = 'ACCOUNT_SUSPENDED',
  PROMO_BUNDLE_EXPIRED = 'PROMO_BUNDLE_EXPIRED',
}

export enum EFinanceNotificationType {
  DEPOSIT = 'DEPOSIT',
  VOUCHER = 'VOUCHER',
  BALANCE_WARN = 'BALANCE_WARN',
  BALANCE_INFO = 'BALANCE_INFO',
  BUNDLE_USAGE = 'BUNDLE_USAGE',
  NEGATIVE_BALANCE = 'NEGATIVE_BALANCE',
  MONTHLY_CREDIT_DEPLETED = 'MONTHLY_CREDIT_DEPLETED',
}

export enum ETechnicalNotificationType {
  ACCOUNT_OFF_LOADED = 'ACCOUNT_OFF_LOADED',
}

export enum EMarketingNotificationType {
  MARKETING = 'MARKETING',
}

export enum ESortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum ESortBy {
  TIMESTAMP = 'TIMESTAMP',
}

export enum ENotificationCategory {
  BILLING = 'BILLING',
  SYSTEM = 'SYSTEM',
  NEWS = 'NEWS',
  UNKNOWN = 'UNKNOWN',
}

export interface IGetNotificationsParams extends IApiUserGroupParams {
  only_unseen?: boolean; // false by default
  sort_by?: ESortBy; // TIMESTAMP by default
  sort_direction?: ESortDirection; // DESC by default
  category?: ENotificationCategory;
  older_than?: Milliseconds;
  cursor?: number;
  limit?: number;
}

export interface IDeliveryItem {
  channel: string;
  sent: boolean;
}

export interface ICommonNotification {
  id: string;
  address: string;
  title: string;
  category: ENotificationCategory;
  message: string;
  seen: boolean;
  deliveries: IDeliveryItem[];
  createdAt: number;
  updatedAt: number;
}

export interface IGlobalNotification extends ICommonNotification {
  type: EGlobalNotificationType;
}

export interface IFinanceNotification extends ICommonNotification {
  type: EFinanceNotificationType;
}

export interface ITechnicalNotification extends ICommonNotification {
  type: ETechnicalNotificationType;
}

export interface IMarketingNotification extends ICommonNotification {
  type: EMarketingNotificationType;
}

export type INotificationItem =
  | IGlobalNotification
  | IFinanceNotification
  | ITechnicalNotification
  | IMarketingNotification;

export interface IGetNotificationsResponse {
  cursor: number;
  notifications: INotificationItem[];
}

export interface IMarkNotificationsAsReadParams extends IApiUserGroupParams {
  seen: boolean;
  ids?: string[];
}

export interface IMarkNotificationsAsReadResponse {
  result: string;
}

export interface IGetNotificationsChannelsParams extends IApiUserGroupParams {
  active_only?: boolean;
}

interface ICreditThreshold {
  value: number;
}

export interface INotificationsChannelConfig {
  deposit?: boolean;
  voucher?: boolean;
  marketing?: boolean;
  credit_alarm?: boolean;
  credit_alarm_threshold?: ICreditThreshold;
  credit_info?: boolean;
  credit_info_threshold?: ICreditThreshold;
  credit_warn?: boolean;
  credit_warn_threshold?: ICreditThreshold;
  account_suspended?: boolean;
  negative_balance?: boolean;
  account_off_loaded?: boolean;
  monthly_credit_depleted?: boolean;
  bundle_usage?: boolean;
  promo_bundle_expired?: boolean;
  super_red_alert?: boolean;
  blockchain_status?: boolean; // chains health status notifications
}

export enum ENotificationChannel {
  TELEGRAM = 'TELEGRAM',
  EMAIL = 'EMAIL',
  SLACK = 'SLACK',
  INAPP = 'INAPP',
}

export interface IGetNotificationsChannelsResponse {
  address: Web3Address;
  channel: ENotificationChannel;
  handle: string;
  username: string;
  is_active: boolean;
  is_group: boolean;
  configs: INotificationsChannelConfig;
}

export interface IUpdateNotificationsChannelParams extends IApiUserGroupParams {
  channel: ENotificationChannel;
  config: INotificationsChannelConfig;
}

export interface IGetMessengerNotificationsBotDataResponse {
  name: string;
  url: string;
}

export interface IGetMessengerNotificationsBotDetailsParams
  extends IApiUserGroupParams {}

export interface IGetMessengerNotificationsBotDetailsResponse {
  team: string;
  channels: string[];
}

export interface IEnableTelegramIntegrationParams extends IApiUserGroupParams {
  confirmation_data?: string;
}

export interface IEnableSlackIntegrationParams extends IApiUserGroupParams {
  code: string;
}

export interface IAddNewEmailForNotificationsParams
  extends IApiUserGroupParams {
  email: string;
}

export interface IConfirmEmailForNotificationsParams
  extends IApiUserGroupParams {
  confirmation_data: string;
}

export interface IEnableMessengerIntegrationResponse {
  result: string;
}

export interface IDeleteNotificationsChannelParams extends IApiUserGroupParams {
  channel: ENotificationChannel;
}

export interface IDeleteNotificationsChannelResponse {
  result: string;
}

export interface IUpdateNotificationsChannelStatusParams
  extends IApiUserGroupParams {
  active: boolean;
  channel: ENotificationChannel;
}

export interface IUpdateNotificationsChannelStatusResponse {
  result: string;
}
